import { Injectable } from '@angular/core';
import { BotidService } from '@shared-angular/services/ragai/botid.service';
import { DialogMessage, HistoryItemResponse } from '@shared/index';
import { dialogStoreIdSchema, dialogStoreKeySchema } from '@shared/ragai.schemas';
import { z } from 'zod';
import { BotService } from './bot.service';
import { MessageManagerService } from './message-manager.service';

/**
 * Speichert und stellt Dialogverläufe im LocalStorage wieder her (`RAGAI.DIALOGSTORE`).
 * @remarks
 * Anders als `RAGAI.DIALOGRECORDER` (Events-basiert, serverseitig via `RAGAI.KEYVAL` persistiert) arbeitet
 * dieser Service rein clientseitig über `localStorage` und operiert auf dem **kompletten** aktuellen
 * Nachrichtenverlauf (`RAGAI.BOTMANAGER`-Historie), nicht auf einzeln mitgeschnittenen Nachrichten. Ein
 * gespeicherter Dialog überlebt daher einen Browser-Neustart auf demselben Gerät, ist aber nicht geräteübergreifend
 * synchronisiert (im Gegensatz zu `RAGAI.KEYVAL`, das serverseitig pro Nutzer gespeichert wird).
 * {@link restoreDialogFromLocalStorage} kann bestehende Verlaufsnachrichten optional vorher löschen
 * (`removeExisting`) oder bereits vorhandene Nachrichten beim Wiederherstellen überspringen (`ignoreExisting`).
 */
@Injectable({
  providedIn: 'root'
})
export class DialogStoreManagerService {

  constructor(
    private botService: BotService,
    private messageMgr: MessageManagerService,
    private botidService: BotidService
  ) { }

  /**
   * Lädt den aktuellen Nachrichtenverlauf des Bots.
   * @returns Der aktuelle Verlauf.
   */
  public async getCurrentDialog() {
    const botId = this.botidService.getBotId();
    return (await this.botService.getHistoryMessages(botId)).messageHistory;
  }

  /**
   * Speichert den aktuellen Dialog im LocalStorage des Browsers.
   * @param key Schlüssel/ID für die Speicherung, oder ein Objekt gemäß `dialogStoreKeySchema`. Ohne Angabe wird eine ID generiert.
   * @returns Die verwendete Speicher-ID.
   */
  public async storeDialogToLocalStorage(key: string | z.input<typeof dialogStoreKeySchema>): Promise<string> {
    const parsed = dialogStoreKeySchema.safeParse(key);
    if (parsed.success && typeof key === 'object') {
      key = parsed.data.key;
    }
    const k = key as string;
    const id = k ? k : "dialog_" + new Date().getTime() + "_" + Math.random() * 10000;
    localStorage.setItem(id, JSON.stringify(await this.getCurrentDialog()));
    return id;
  }
  // removeExisting: if true, all messages in the current dialog will be deleted, before restoring
  // ignoreExisting: if true, messages that are already in the dialog will not be restored
  /**
   * Stellt einen zuvor gespeicherten Dialog aus dem LocalStorage wieder her.
   * @param dialogId ID des gespeicherten Dialogs, oder ein Objekt gemäß `dialogStoreIdSchema`.
   * @param removeExisting `true`, um den aktuellen Verlauf vorher zu löschen.
   * @param ignoreExisting `true`, um bereits vorhandene Nachrichten nicht erneut hinzuzufügen.
   * @returns Die hinzugefügten Verlaufsnachrichten, oder `null` wenn kein Dialog gefunden wurde.
   */
  public async restoreDialogFromLocalStorage(dialogId: string | z.input<typeof dialogStoreIdSchema>, removeExisting: boolean = false, ignoreExisting: boolean = true): Promise<unknown> {
    const parsed = dialogStoreIdSchema.safeParse(dialogId);
    if (parsed.success && typeof dialogId === 'object') {
      dialogId = parsed.data.dialogId;
    }
    const id = dialogId as string;
    if (removeExisting) {
      const dialog = await this.getCurrentDialog();
      const botId = this.botidService.getBotId();
      if (botId && dialog.length > 0) {
        await this.botService.deleteHistoryMessages(botId, dialog.map(m => m._id));
      }
    }
    const str = localStorage.getItem(id);
    if (str == null) {
      console.warn("No Dialog to restore found with id " + id);
      return null;
    }
    const historyItems: HistoryItemResponse[] = JSON.parse(str) as HistoryItemResponse[];
    const addedHistoryItems = await this.addDialogMessagesFromHistoryItems(historyItems, ignoreExisting);
    return addedHistoryItems;
  }
  /**
   * Wandelt einen Verlaufseintrag in eine Dialog-Nachricht um.
   * @param item Der Verlaufseintrag.
   * @returns Die Dialog-Nachricht.
   */
  public createDialogMessageFromHistoryItem(item: HistoryItemResponse): DialogMessage {
    return {
      content: item.content,
      role: item.role,
      bypass: item.bypass
    };
  }
  /**
   * Fügt mehrere Verlaufseinträge dem aktuellen Dialog hinzu.
   * @param historyItems Die hinzuzufügenden Verlaufseinträge.
   * @param ignoreExisting `true`, um bereits vorhandene Einträge zu überspringen.
   * @returns Die gespeicherten Nachrichten.
   */
  public async addDialogMessagesFromHistoryItems(historyItems: HistoryItemResponse[], ignoreExisting: boolean) {
    if (ignoreExisting) {
      const currentHistoryItems = await this.getCurrentDialog();
      const currentIds = currentHistoryItems.map(m => m._id);
      historyItems = historyItems.filter(m => !currentIds.includes(m._id));
    }
    const messages = historyItems.map(item => this.createDialogMessageFromHistoryItem(item));
    return await this.messageMgr._addMessagesToThread(messages);
  }
  /**
   * Ersetzt den aktuellen Dialog vollständig durch einen gespeicherten Dialog.
   * @param dialogId ID des gespeicherten Dialogs.
   * @returns Die hinzugefügten Verlaufsnachrichten.
   */
  public async overrideDialogFromLocalStorage(dialogId: string) {
    return await this.restoreDialogFromLocalStorage(dialogId, true, false);
  }

  /**
   * Hängt einen gespeicherten Dialog an den aktuellen Verlauf an (bereits vorhandene Nachrichten werden ausgelassen).
   * @param dialogId ID des gespeicherten Dialogs.
   * @returns Die hinzugefügten Verlaufsnachrichten.
   */
  public async appendDialogFromLocalStorage(dialogId: string) {
    return await this.restoreDialogFromLocalStorage(dialogId, false, true);
  }
  /**
   * Hängt einen gespeicherten Dialog vollständig an, auch wenn Nachrichten bereits vorhanden sind.
   * @param dialogId ID des gespeicherten Dialogs.
   * @returns Die hinzugefügten Verlaufsnachrichten.
   */
  public async appendDialogFromLocalStorageIgnoreExisting(dialogId: string) {
    return await this.restoreDialogFromLocalStorage(dialogId, false, false);
  }
}
